/**
 * PaymentModal.tsx
 *
 * Modal component for payment provider selection and processing
 * Handles Paystack and SnapScan payment flows
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useFormBuilderContext } from '../contexts/FormBuilderContext';
import { initPaystackPayment, initSnapScanPayment, verifyPayment, pollPaymentStatus, initPaystackPaymentRedirect, initExternalPayment, verifyExternalPayment, fetchPlanDetails, openPaystackPopupWithAccessCode } from '../services/paymentService';
import { formatCurrency } from '../utils/paymentUtils';
import { loadPaystackSDKWithRetry, isPaystackSDKLoaded } from '../utils/loadPaystackSDK';
import type { PaymentProvider, PaymentMode, CustomerSyncResponse, ExternalPaymentInitResponse } from '../types/payment-types';

/**
 * Inject CSS to override Paystack popup z-index and disable Dialog overlay when payment modal open
 * Ensures Paystack iframe appears above Dialog overlays and payment modal is not blocked
 */
function injectPaystackZIndexOverride() {
  const styleId = 'paystack-z-index-override';

  // Check if already injected
  if (document.getElementById(styleId)) {
    return;
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    /* Ensure the Paystack iframe itself is elevated */
    iframe[src*="paystack.com"],
    iframe[src*="checkout.paystack.com"] {
      z-index: 2147483647 !important;
      pointer-events: auto !important;
    }

    /* Disable Dialog overlay pointer events when payment modal is open */
    body.payment-modal-open [data-dialog-overlay="true"] {
      pointer-events: none !important;
    }

    /* Hide parent app's page loader when payment modal is open */
    body.payment-modal-open .page-loader-wrapper {
      display: none !important;
      pointer-events: none !important;
    }

    /* Lower parent app's fixed sidebar and any z-50 elements when popup is active */
    body.paystack-popup-active .z-50,
    body.paystack-popup-active aside[class*="z-50"],
    body.paystack-popup-active [class*="z-50"][style*="position: fixed"],
    body.paystack-popup-active [class*="z-50"][class*="fixed"] {
      z-index: 0 !important;
    }
  `;
  document.head.appendChild(style);
}

interface PaymentModalProps {
  isOpen: boolean;
  fieldId: string;
  amount: number; // Amount in cents
  currency?: string;
  description?: string;
  metadata?: Record<string, any>;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  onClose: (status?: 'cancelled' | 'failed') => void;
  onSuccess: (transactionId: string, reference: string, provider: PaymentProvider, customerData?: { firstName: string; lastName: string; email: string; phone: string }, verificationResult?: any) => void;
  onError: (error: string) => void;
  paystackPublicKey?: string; // Optional override for preview mode
  apiBaseUrl?: string; // Optional override for preview mode
  formEntryId?: number | string; // Form entry ID for linking payment to submission
  formVersionId?: number | string; // Form version ID for tracking which form was used
  customPaymentEndpoint?: string; // Webhook URL for external mode, or custom endpoint for built-in
  planCode?: string; // Optional Paystack plan code for subscription payments
  paymentMode?: PaymentMode; // 'built-in' (default) or 'external' (webhook-based)
  verificationEndpoint?: string; // Verification webhook URL for external payment mode
  /**
   * The form's answers, keyed by field id. Merged into the fallback
   * initialize payload so a server that prices from a form answer can see it
   * even when the page-submission preload did not run.
   */
  formAnswers?: Record<string, any>;
  preloadedAccessCode?: string; // Access code pre-initialized via webhook_call_on_page_submit
  preloadedReference?: string; // Reference pre-initialized via webhook_call_on_page_submit
  /**
   * Amount in MAJOR units that the webhook bound to the pre-initialized access
   * code. In external mode the server calls Paystack's transaction/initialize
   * itself, so it — not amountConfig — knows what will actually be charged.
   * When supplied this takes precedence over the field's configured amount.
   */
  preloadedAmount?: number;
  sessionId?: string; // Session ID for linking payment to the form session
  userId?: string; // User ID for tracking
  formId?: string; // Form definition ID
  pageNumber?: number; // Current page number
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  fieldId,
  amount,
  currency = 'ZAR',
  description,
  metadata = {},
  email = '', // Default email, should be passed from user context
  onClose,
  onSuccess,
  onError,
  paystackPublicKey: overridePaystackKey,
  apiBaseUrl: overrideApiBaseUrl,
  formEntryId,
  formVersionId,
  customPaymentEndpoint,
  firstName: propFirstName = '',
  lastName: propLastName = '',
  phone: propPhone = '',
  planCode,
  paymentMode = 'built-in',
  verificationEndpoint,
  formAnswers,
  preloadedAccessCode,
  preloadedReference,
  preloadedAmount,
  sessionId,
  userId,
  formId,
  pageNumber,
}) => {
  const context = useFormBuilderContext();
  const appConfig = context?.appConfig;
  const [selectedProvider, setSelectedProvider] = useState<PaymentProvider | null>(null);
  const [processing, setProcessing] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState(email);
  const [firstName, setFirstName] = useState(propFirstName);
  const [lastName, setLastName] = useState(propLastName);
  const [phone, setPhone] = useState(propPhone);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [paymentStage, setPaymentStage] = useState<'email' | 'details' | 'ready'>('email');
  const [customerSyncing, setCustomerSyncing] = useState(false);
  const [resolvedAmount, setResolvedAmount] = useState<number>(amount);
  const [fetchingPlan, setFetchingPlan] = useState(false);
  const [planInterval, setPlanInterval] = useState<string | null>(null);

  // Resolve the payment API base URL: prefer payment-specific config, then backend URL (derived from config fetch), then prop override, then general app URL
  const paymentApiUrl = appConfig?.features?.payment?.apiBaseUrl || appConfig?.backend_api_base_url || overrideApiBaseUrl || appConfig?.api_base_url || '';

  // True only when a dedicated payment/customer backend is explicitly configured.
  // The general api_base_url (passed via the apiBaseUrl prop) is NOT sufficient —
  // that's just the form app URL, not a payment backend with a customer DB.
  // Customer sync and the details-collection stages only make sense when this is set.
  const hasPaymentBackend = !!(appConfig?.features?.payment?.apiBaseUrl || appConfig?.backend_api_base_url);

  /**
   * Verify payment with retry logic.
   * Paystack's callback fires before the transaction is fully settled,
   * so immediate verification may return 'cancelled' or 'pending'.
   * Retries up to maxRetries times with a delay between attempts.
   */
  const verifyPaymentWithRetry = useCallback(async (
    reference: string,
    provider: 'paystack' | 'snapscan',
    apiUrl: string,
    applicationId: string,
    fvId?: number | string,
    maxRetries: number = 3,
    delayMs: number = 2000
  ) => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      // Wait before each attempt (including the first) to let Paystack settle
      await new Promise(resolve => setTimeout(resolve, delayMs));

      console.log(`🔍 Verification attempt ${attempt + 1}/${maxRetries + 1} for ref: ${reference}`);
      const result = await verifyPayment(reference, provider, apiUrl, applicationId, fvId);

      if (result.success && result.transaction?.status === 'completed') {
        return result;
      }

      console.log(`⏳ Attempt ${attempt + 1}: status=${result.transaction?.status || 'unknown'}, retrying...`);
    }

    // Return last attempt result even if not completed
    return verifyPayment(reference, provider, apiUrl, applicationId, fvId);
  }, []);

  /**
   * Sync customer data with backend and handle stage transitions
   * Two-stage flow: email -> auto-fill if found -> show details form if needed
   */
  const syncCustomer = useCallback(async (email: string) => {
    if (!email || !email.includes('@')) return;

    // Only sync when a dedicated payment backend is configured (external DB connected).
    // Without one there's nothing to sync against — skip customer collection entirely.
    if (!hasPaymentBackend) {
      setPaymentStage('details');
      return;
    }

    setCustomerSyncing(true);
    try {
      const applicationId = appConfig?.app_id || 'app-12345';

      const response = await fetch(`${paymentApiUrl}/api/v1/customers/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          application_id: applicationId,
          form_version_id: formVersionId,
        }),
      });

      if (response.ok) {
        const result: CustomerSyncResponse = await response.json();
        if (result.code === 200 && result.data) {
          const customer = result.data;

          // Check for data conflicts
          if (customer.data_conflict) {
            // Data mismatch detected - clear all fields and show details form
            setFirstName('');
            setLastName('');
            setPhone('');
            setCustomerId(null);
            setPaymentStage('details'); // Show details form for re-entry
            console.log('Customer data conflict - showing details form for re-entry');
          } else if (customer.first_name || customer.last_name || customer.phone) {
            // Customer found with details - auto-fill and skip to payment
            if (customer.first_name) setFirstName(customer.first_name);
            if (customer.last_name) setLastName(customer.last_name);
            if (customer.phone) setPhone(customer.phone);
            setCustomerId(customer.customer_id);
            setPaymentStage('ready'); // Skip details form, customer is ready
            console.log('Customer found - auto-filled details, ready for payment');
          } else {
            // Customer exists but no details - show details form
            setPaymentStage('details');
            console.log('Customer found but no details - showing details form');
          }
        } else {
          // No customer found - show details form
          setPaymentStage('details');
          console.log('No customer found - showing details form');
        }
      } else {
        // API error - show details form to allow manual entry
        setPaymentStage('details');
        console.warn('Customer sync API error - showing details form');
      }
    } catch (error) {
      console.warn('Customer sync failed (non-blocking):', error);
      // On error, show details form to allow manual entry
      setPaymentStage('details');
    } finally {
      setCustomerSyncing(false);
    }
  }, [appConfig, overrideApiBaseUrl]);

  // Inject CSS immediately on component mount (not waiting for SDK load)
  useEffect(() => {
    console.log('🔧 PaymentModal: Injecting CSS on component mount');
    injectPaystackZIndexOverride();
    console.log('✅ PaymentModal: CSS injection complete');
  }, []);

  // Resolve the amount to display, in precedence order:
  //   1. preloadedAmount — what the webhook actually bound to the access code
  //   2. the plan lookup, when a planCode is configured and no amount is set
  //   3. the field's configured amount
  //
  // (1) is handled inside this effect rather than a separate one ON PURPOSE.
  // This effect already calls setResolvedAmount on its early-return path, so a
  // second effect would race it and the winner would depend on declaration
  // order and which deps happened to change. Precedence stated here is
  // precedence you can read.
  //
  // Major -> minor with Math.round, not a bare *100: 309.29 * 100 is
  // 30928.999999999996 in IEEE-754, and truncating it would undercharge the
  // display by a cent against the figure Paystack shows.
  const preloadedMinor =
    typeof preloadedAmount === 'number' && preloadedAmount > 0
      ? Math.round(preloadedAmount * 100)
      : null;

  useEffect(() => {
    if (preloadedMinor !== null) {
      setResolvedAmount(preloadedMinor);
      setFetchingPlan(false);
      return;
    }

    if (!isOpen || !planCode || amount > 0) {
      setResolvedAmount(amount);
      setFetchingPlan(false);
      setPlanInterval(null);
      return;
    }

    if (!paymentApiUrl) {
      setResolvedAmount(0);
      return;
    }

    setFetchingPlan(true);
    fetchPlanDetails(planCode, paymentApiUrl).then((planDetails) => {
      if (planDetails && planDetails.amount > 0) {
        setResolvedAmount(planDetails.amount);
        setPlanInterval(planDetails.interval || null);
      } else {
        setResolvedAmount(0);
      }
      setFetchingPlan(false);
    });
  }, [isOpen, planCode, amount, paymentApiUrl, preloadedMinor]);

  // Detect payment callback from Paystack redirect
  useEffect(() => {
    // Check if we just returned from Paystack (URL will have trxref and reference params)
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const trxref = urlParams.get('trxref');
    const reference = urlParams.get('reference');
    const paymentFieldId = urlParams.get('payment_field_id');
    const paymentMode = urlParams.get('payment_mode');

    if (trxref && reference) {
      console.log('🔄 Detected Paystack callback with reference:', reference);
      console.log('📋 Payment context from URL:', { paymentFieldId, paymentMode });

      // Try to retrieve payment context from sessionStorage
      let paymentContext = null;
      const paymentContextStr = sessionStorage.getItem('paymentContext');
      if (paymentContextStr) {
        try {
          paymentContext = JSON.parse(paymentContextStr);

          // Verify this is a recent payment (within last 10 minutes)
          const ageMinutes = (Date.now() - paymentContext.timestamp) / (1000 / 60);
          if (ageMinutes >= 10) {
            console.warn('⚠️ Payment context expired (older than 10 minutes)');
            paymentContext = null;
            sessionStorage.removeItem('paymentContext');
          }
        } catch (err) {
          console.error('❌ Failed to parse payment context:', err);
          paymentContext = null;
        }
      }

      // Fallback: Reconstruct payment context from URL params if sessionStorage is empty/expired
      if (!paymentContext && paymentFieldId) {
        console.log('⚠️ SessionStorage missing/expired, reconstructing context from URL params');
        paymentContext = {
          reference,
          fieldId: paymentFieldId,
          paymentMode: paymentMode || 'external',
          verificationEndpoint,
          timestamp: Date.now(),
          customerData: { firstName, lastName, email: userEmail, phone },
          metadata: {},
        };
      }

      if (paymentContext) {
        console.log('✅ Payment context available, verifying payment...');

        // Clean up URL params immediately
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);

        // Verify payment with backend
        handlePaymentCallback(reference, paymentContext);
      } else {
        console.error('❌ Cannot verify payment - no context available');
        // Clean up URL params even on error
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    }
  }, []); // Run once on mount

  // Handle payment callback verification
  const handlePaymentCallback = async (reference: string, paymentContext: any) => {
    try {
      console.log('🔍 Verifying payment...');
      setVerifying(true);

      const customerData = paymentContext.customerData || { firstName, lastName, email: userEmail, phone };

      // Branch based on the explicit payment mode stored at init time
      if (paymentContext.paymentMode === 'external') {
        // External mode — verify via the external provider's verification endpoint
        console.log('🔗 External payment mode detected, using external verification...');

        const externalResult = await verifyExternalPayment(
          paymentContext.verificationEndpoint,
          reference
        );

        // Clean up payment context
        sessionStorage.removeItem('paymentContext');

        switch (externalResult.status) {
          case 'completed':
            console.log('✅ External payment verified as completed!');
            onSuccess(
              externalResult.transactionId || reference,
              reference,
              'paystack',
              customerData,
              externalResult
            );
            toast.success('Payment completed successfully!');
            break;

          case 'pending':
            console.log('⏳ External payment accepted as pending');
            onSuccess(
              externalResult.transactionId || reference,
              reference,
              'paystack',
              customerData,
              externalResult
            );
            toast.info('Payment received, verification pending.');
            break;

          case 'failed':
          case 'cancelled': {
            const errorMsg = externalResult.error || `Payment ${externalResult.status}. Please try again.`;
            console.error('❌ External payment verification:', externalResult.status);
            setError(errorMsg);
            onError(errorMsg);
            onClose('failed');
            break;
          }
        }
      } else {
        // Built-in mode — verify via our own backend
        const appId = appConfig?.app_id || paymentContext.metadata?.application_id || 'app-12345';

        const result = await verifyPayment(reference, 'paystack', paymentApiUrl, appId, paymentContext.formVersionId);

        if (result && result.transaction && result.transaction.status === 'completed') {
          console.log('✅ Payment verified successfully!');

          // Clean up payment context
          sessionStorage.removeItem('paymentContext');

          onSuccess(
            result.transaction.transactionId || result.transaction.reference,
            result.transaction.reference,
            'paystack',
            customerData,
            result
          );

          toast.success('Payment completed successfully!');
        } else {
          console.error('❌ Payment verification failed or payment not completed');
          const errorMsg = 'Payment verification failed. Please contact support.';
          setError(errorMsg);
          onError(errorMsg);
          onClose('failed');
        }
      }
    } catch (err) {
      console.error('❌ Payment verification error:', err);
      const errorMsg = 'Payment verification failed. Please try again or contact support.';
      setError(errorMsg);
      onError(errorMsg);
      onClose('failed');
    } finally {
      setVerifying(false);
    }
  };

  // Reset state when modal opens and manage body class
  useEffect(() => {
    if (isOpen) {
      // Auto-select Paystack since it's the only provider
      setSelectedProvider('paystack');
      setProcessing(false);
      setError(null);
      setQrCodeUrl(null);
      setShowEmailInput(true); // Show email input immediately
      setUserEmail(email);
      setFirstName(propFirstName);
      setLastName(propLastName);
      setPhone(propPhone);
      setPaymentStage('email'); // Start at email stage
      setCustomerId(null);
      setCustomerSyncing(false);

      // Add body class to disable Dialog overlay pointer events
      console.log('🎭 PaymentModal: Adding body class "payment-modal-open"');
      document.body.classList.add('payment-modal-open');
      console.log('🎭 PaymentModal: Body classes:', document.body.className);

      // AGGRESSIVE FIX: Directly disable pointer-events on Dialog overlays
      const dialogOverlays = document.querySelectorAll('[data-dialog-overlay="true"]');
      console.log(`🔍 Found ${dialogOverlays.length} Dialog overlays`);
      dialogOverlays.forEach((overlay, index) => {
        console.log(`🚫 Disabling pointer-events on Dialog overlay #${index}`);
        (overlay as HTMLElement).style.pointerEvents = 'none';
      });

      // CRITICAL FIX: Hide parent app's page loader that blocks Paystack (z-index: 99999999)
      const pageLoader = document.querySelector('.page-loader-wrapper');
      if (pageLoader) {
        console.log('🚨 Found parent app page loader - hiding it');
        (pageLoader as HTMLElement).style.display = 'none';
      }
    } else {
      // Remove body class when modal closes
      document.body.classList.remove('payment-modal-open');

      // Restore pointer-events on Dialog overlays
      const dialogOverlays = document.querySelectorAll('[data-dialog-overlay="true"]');
      dialogOverlays.forEach((overlay) => {
        (overlay as HTMLElement).style.pointerEvents = '';
      });

      // Restore parent app's page loader
      const pageLoader = document.querySelector('.page-loader-wrapper');
      if (pageLoader) {
        (pageLoader as HTMLElement).style.display = '';
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('payment-modal-open');

      // Restore pointer-events on Dialog overlays
      const dialogOverlays = document.querySelectorAll('[data-dialog-overlay="true"]');
      dialogOverlays.forEach((overlay) => {
        (overlay as HTMLElement).style.pointerEvents = '';
      });

      // Restore parent app's page loader
      const pageLoader = document.querySelector('.page-loader-wrapper');
      if (pageLoader) {
        (pageLoader as HTMLElement).style.display = '';
      }
    };
  }, [isOpen, email]);

  // Don't render if not open
  if (!isOpen) return null;

  // Ensure we're in the browser
  if (typeof document === 'undefined') return null;

  // DEBUG: Log config resolution
  console.log('💳 PaymentModal - Config Resolution:', {
    hasOverrideKey: !!overridePaystackKey,
    overrideKey: overridePaystackKey,
    appConfigExists: !!appConfig,
    appConfigFeatures: appConfig?.features,
    appConfigPayment: appConfig?.features?.payment,
    envVar: process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY,
  });

  // Use override props if provided, otherwise use appConfig, otherwise fallback to env vars
  const paymentConfig = overridePaystackKey ? {
    paystackEnabled: true,
    paystackPublicKey: overridePaystackKey,
  } : appConfig?.features?.payment || {
    // DEVELOPMENT FALLBACK: Remove this in production!
    paystackEnabled: true,
    paystackPublicKey: process.env.NEXT_PUBLIC_PAYSTACK_PUBLIC_KEY || 'pk_test_PLACEHOLDER',
  };

  console.log('💳 PaymentModal - Final Payment Config:', paymentConfig);

  // Handle Paystack payment with inline popup
  const handlePaystackPayment = async () => {
    console.log('💳 Payment Configuration (Inline Popup):', {
      apiUrl: paymentApiUrl,
      amount: resolvedAmount,
      email: userEmail,
      currency,
      hasPlanCode: !!planCode,
    });

    setSelectedProvider('paystack');
    setError(null);
    setProcessing(true);

    try {
      const paystackKey = paymentConfig?.paystackPublicKey;
      if (!paystackKey) {
        throw new Error('Paystack public key is not configured');
      }

      // Load Paystack SDK
      await loadPaystackSDKWithRetry();

      const paymentMetadata = {
        fieldId,
        form_entry_id: formEntryId,
        form_version_id: formVersionId,
        application_id: appConfig?.app_id || 'app-12345',
        first_name: firstName,
        last_name: lastName,
        phone: phone,
        ...metadata,
      };

      const customerData = { firstName, lastName, email: userEmail, phone };

      if (planCode) {
        // With plan code: use backend to initialize (plan association happens server-side)
        // then open popup with the returned access_code
        console.log('🚀 Initializing plan-based payment via backend...');

        const paymentReference = `PAY-${fieldId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        const result = await initPaystackPaymentRedirect(
          resolvedAmount,
          userEmail,
          { ...paymentMetadata, reference: paymentReference },
          typeof window !== 'undefined' ? window.location.href : 'http://localhost:3000',
          { first_name: firstName, last_name: lastName, phone },
          paymentApiUrl,
          currency,
          formVersionId,
          planCode
        );

        console.log('✅ Backend returned access_code, opening popup...');

        openPaystackPopupWithAccessCode(
          paystackKey,
          result.access_code,
          userEmail,
          resolvedAmount,
          currency,
          async (response: any) => {
            console.log('✅ Payment successful (plan-based popup):', response);
            const ref = response.reference || result.reference;
            setVerifying(true);
            try {
              const appId = appConfig?.app_id || 'app-12345';
              const verificationResult = await verifyPaymentWithRetry(
                ref,
                'paystack',
                paymentApiUrl,
                appId,
                formVersionId
              );

              if (verificationResult.success && verificationResult.transaction?.status === 'completed') {
                onSuccess(
                  verificationResult.transaction.transactionId || ref,
                  ref,
                  'paystack',
                  customerData,
                  verificationResult
                );
                toast.success('Payment completed successfully!');
              } else {
                const errorMsg = 'Payment verification failed. Please contact support.';
                setError(errorMsg);
                onError(errorMsg);
                onClose('failed');
              }
            } catch (verifyErr) {
              console.error('❌ Payment verification error:', verifyErr);
              const errorMsg = 'Payment verification failed. Please try again.';
              setError(errorMsg);
              onError(errorMsg);
              onClose('failed');
            } finally {
              setVerifying(false);
            }
            setProcessing(false);
          },
          () => {
            console.log('ℹ️ Payment popup closed by user');
            setProcessing(false);
            setSelectedProvider(null);
            onClose('cancelled');
          }
        );
      } else {
        // Without plan code: use inline popup directly with public key
        console.log('🚀 Opening Paystack inline popup...');

        const result = await initPaystackPayment(
          paystackKey,
          resolvedAmount,
          userEmail,
          paymentMetadata,
          async (response: any) => {
            console.log('✅ Payment successful (inline popup):', response);
            const ref = response.reference;
            setVerifying(true);
            try {
              const appId = appConfig?.app_id || 'app-12345';
              const verificationResult = await verifyPaymentWithRetry(
                ref,
                'paystack',
                paymentApiUrl,
                appId,
                formVersionId
              );

              if (verificationResult.success && verificationResult.transaction?.status === 'completed') {
                onSuccess(
                  verificationResult.transaction.transactionId || ref,
                  ref,
                  'paystack',
                  customerData,
                  verificationResult
                );
                toast.success('Payment completed successfully!');
              } else {
                const errorMsg = 'Payment verification failed. Please contact support.';
                setError(errorMsg);
                onError(errorMsg);
                onClose('failed');
              }
            } catch (verifyErr) {
              console.error('❌ Payment verification error:', verifyErr);
              const errorMsg = 'Payment verification failed. Please try again.';
              setError(errorMsg);
              onError(errorMsg);
              onClose('failed');
            } finally {
              setVerifying(false);
            }
            setProcessing(false);
          },
          () => {
            console.log('ℹ️ Payment popup closed by user');
            setProcessing(false);
            setSelectedProvider(null);
            onClose('cancelled');
          },
          {
            first_name: firstName,
            last_name: lastName,
            phone: phone,
          },
          currency
        );

        console.log('✅ Paystack popup opened, reference:', result.reference);
      }
    } catch (err) {
      console.error('❌ Failed to initialize Paystack payment:', err);
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      onError(errorMessage);
      setProcessing(false);
      setSelectedProvider(null);
    }
  };

  // Handle external payment via webhook
  // POSTs customer data to webhook → gets access_code → opens Paystack popup
  const handleExternalPayment = async () => {
    if (!customPaymentEndpoint) {
      setError('Webhook URL is not configured for external payment mode');
      onError('Webhook URL is not configured');
      return;
    }

    setSelectedProvider('paystack');
    setError(null);
    setProcessing(true);

    try {
      const paystackKey = paymentConfig?.paystackPublicKey;
      if (!paystackKey) {
        throw new Error('Paystack public key is not configured');
      }

      // Load Paystack SDK
      await loadPaystackSDKWithRetry();

      console.log('🔍 handleExternalPayment - preloadedAccessCode:', preloadedAccessCode, '| preloadedReference:', preloadedReference);

      let result: ExternalPaymentInitResponse;

      if (preloadedAccessCode && preloadedReference) {
        // Use the access_code pre-initialized during page submission (avoids a second webhook call)
        console.log('✅ Using pre-initialized Paystack access_code from page submission webhook');
        result = { access_code: preloadedAccessCode, reference: preloadedReference };
      } else {
        console.log('🔗 Initializing external payment via webhook...');

        // POST form data to the external webhook using the same structure as page submissions
        result = await initExternalPayment(customPaymentEndpoint, {
          form_id: formId || formVersionId?.toString() || '',
          form_version_id: formVersionId || null,
          page_number: pageNumber || 0,
          field_id: fieldId,
          session_id: sessionId || null,
          user_id: userId || 'unknown',
          answers: {
            // The form's own answers FIRST, so the synthetic keys below always
            // win their own names (email, amount, currency, …) while every
            // field-id key survives. Without these the server sees only a
            // name and an email, and prices from a default — on the accept
            // form that meant charging against the practice's billing date
            // instead of the one the member picked.
            ...(formAnswers || {}),
            email: userEmail,
            amount: resolvedAmount,
            currency,
            firstName,
            lastName,
            phone,
            planCode,
            metadata: {
              step: 'initialize',
              fieldId,
              form_version_id: formVersionId,
              application_id: appConfig?.app_id || 'app-12345',
            },
            ...(planCode ? { plan_code: planCode } : {}),
          },
          form_completed: false,
          source: 'page_submission',
          ...(planCode ? { plan_code: planCode, planCode } : {}),
        });
      }

      // Fallback for the path where the page-submission preload did not happen:
      // take the amount the webhook just bound to this access code. The Paystack
      // popup opens straight after, so this rarely changes what the user reads —
      // it matters when the popup fails to open or is dismissed, where the modal
      // stays on screen and would otherwise keep showing the placeholder.
      if (typeof result.amount === 'number' && result.amount > 0) {
        setResolvedAmount(Math.round(result.amount * 100));
      }

      console.log('✅ Webhook result:', { access_code: result.access_code, reference: result.reference, authorization_url: result.authorization_url, amount: result.amount, resolvedAmount, currency, planCode });

      // Active subscription bypass — no payment popup needed
      if (result.requires_payment === false) {
        console.log('🎫 Active subscription detected in PaymentModal — completing without payment popup');
        const subCode = result.subscription_code || result.reference;
        const subResult = result._rawWebhookResult || { success: true, requires_payment: false, subscription_code: subCode, status: result.status };
        setProcessing(false);
        onSuccess(subCode, subCode, 'paystack', { firstName, lastName, email: userEmail, phone }, subResult);
        toast.success('Active subscription verified! Submitting form...');
        return;
      }

      const customerData = { firstName, lastName, email: userEmail, phone };

      // Open Paystack inline popup.
      // When planCode is set, openPaystackPopupWithAccessCode passes `plan: planCode`
      // to PaystackPop.setup() instead of `access_code`, which avoids the SDK crash
      // ("Cannot destructure property 'language' of 'object null'") that occurs
      // when access_code comes from a plan/subscription transaction.
      openPaystackPopupWithAccessCode(
        paystackKey,
        result.access_code,
        userEmail,
        resolvedAmount,
        currency,
        async (response: any) => {
          console.log('✅ External payment successful (popup):', response);
          const ref = response.reference || result.reference;
          setVerifying(true);
          try {
            // Try external verification endpoint first, fall back to built-in
            if (verificationEndpoint) {
              // Wait for Paystack to settle before verifying externally
              await new Promise(resolve => setTimeout(resolve, 2000));

              const externalResult = await verifyExternalPayment(
                verificationEndpoint,
                ref
              );

              switch (externalResult.status) {
                case 'completed':
                  console.log('✅ External payment verified as completed!');
                  onSuccess(
                    externalResult.transactionId || ref,
                    ref,
                    'paystack',
                    customerData,
                    externalResult
                  );
                  toast.success('Payment completed successfully!');
                  break;

                case 'pending':
                  console.log('⏳ External payment accepted as pending');
                  onSuccess(
                    externalResult.transactionId || ref,
                    ref,
                    'paystack',
                    customerData,
                    externalResult
                  );
                  toast.info('Payment received, verification pending.');
                  break;

                case 'failed':
                case 'cancelled': {
                  const errorMsg = externalResult.error || `Payment ${externalResult.status}. Please try again.`;
                  setError(errorMsg);
                  onError(errorMsg);
                  onClose('failed');
                  break;
                }
              }
            } else {
              // No external verification endpoint — verify via built-in backend with retry
              const appId = appConfig?.app_id || 'app-12345';
              const verificationResult = await verifyPaymentWithRetry(
                ref,
                'paystack',
                paymentApiUrl,
                appId,
                formVersionId
              );

              if (verificationResult.success && verificationResult.transaction?.status === 'completed') {
                onSuccess(
                  verificationResult.transaction.transactionId || ref,
                  ref,
                  'paystack',
                  customerData,
                  verificationResult
                );
                toast.success('Payment completed successfully!');
              } else {
                const errorMsg = 'Payment verification failed. Please contact support.';
                setError(errorMsg);
                onError(errorMsg);
                onClose('failed');
              }
            }
          } catch (verifyErr) {
            console.error('❌ External payment verification error:', verifyErr);
            const errorMsg = 'Payment verification failed. Please try again.';
            setError(errorMsg);
            onError(errorMsg);
            onClose('failed');
          } finally {
            setVerifying(false);
          }
          setProcessing(false);
        },
        () => {
          console.log('ℹ️ External payment popup closed by user');
          setProcessing(false);
          setSelectedProvider(null);
          onClose('cancelled');
        },
        planCode,
        result.reference
      );
    } catch (err) {
      console.error('❌ External payment failed:', err);
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      onError(errorMessage);
      setProcessing(false);
      setSelectedProvider(null);
    }
  };

  // Handle SnapScan payment
  const handleSnapScanPayment = async () => {
    if (!paymentConfig?.snapScanConfig?.merchantId) {
      setError('SnapScan is not configured');
      onError('SnapScan is not configured');
      return;
    }

    setSelectedProvider('snapscan');
    setProcessing(true);
    setError(null);

    try {
      const paymentMetadata = {
        fieldId,
        ...metadata,
      };

      const result = await initSnapScanPayment(
        paymentConfig.snapScanConfig.merchantId,
        resolvedAmount,
        paymentMetadata
      );

      setQrCodeUrl(result.qrCodeUrl);

      // Poll for payment completion
      // In production, you'd poll your backend to check payment status
      if (appConfig?.api_base_url) {
        const transaction = await pollPaymentStatus(
          result.reference,
          'snapscan',
          appConfig.api_base_url,
          appConfig.app_id || 'app-12345',
          60, // Max 60 attempts (2 minutes)
          2000 // Every 2 seconds
        );

        onSuccess(transaction.transactionId || result.reference, result.reference, 'snapscan', { firstName, lastName, email: userEmail, phone }, { success: true, transaction });
        setProcessing(false);
        setQrCodeUrl(null);
      } else {
        // Fallback: simulate success after delay
        setTimeout(() => {
          onSuccess(result.reference, result.reference, 'snapscan', { firstName, lastName, email: userEmail, phone }, { success: true, transaction: { status: 'completed', reference: result.reference } });
          setProcessing(false);
          setQrCodeUrl(null);
        }, 5000);
      }
    } catch (err) {
      const errorMessage = (err as Error).message;
      setError(errorMessage);
      onError(errorMessage);
      setProcessing(false);
      setSelectedProvider(null);
      setQrCodeUrl(null);
    }
  };

  // Check if Paystack popup is open by looking for the iframe
  const isPaystackPopupOpen = typeof document !== 'undefined' &&
    document.querySelector('iframe[src*="checkout.paystack.com"]') !== null;

  const modalContent = (
    <>
      {/* Backdrop overlay */}
      {!processing && !isPaystackPopupOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          style={{ zIndex: 998 }}
          onClick={() => onClose('cancelled')}
        />
      )}

      {/* Modal container - hide entirely when Paystack popup is active */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{
          zIndex: 999,
          // Hide the modal when Paystack popup is open so it doesn't cover the popup
          ...(processing && selectedProvider === 'paystack'
            ? { visibility: 'hidden' as const, pointerEvents: 'none' as const }
            : { pointerEvents: 'auto' as const }
          ),
        }}
        data-payment-modal="true"
        onClick={(e) => {
          if (e.target === e.currentTarget && !processing) {
            onClose('cancelled');
          }
        }}
      >
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
        >
          {/* Close button */}
          {!processing && (
            <button
              onClick={() => onClose('cancelled')}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Complete Payment</h2>
          </div>

          {/* Verifying overlay */}
          {verifying && (
            <div className="absolute inset-0 bg-white bg-opacity-95 rounded-lg flex flex-col items-center justify-center z-10">
              <div className="flex flex-col items-center space-y-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900">Verifying Payment...</p>
                  <p className="text-sm text-gray-600 mt-1">Please wait while we confirm your transaction</p>
                </div>
              </div>
            </div>
          )}

          {/* Amount display */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Amount Due</div>
            <div className="text-3xl font-bold text-gray-900">
              {fetchingPlan ? (
                <span className="text-lg text-gray-400">Loading plan details...</span>
              ) : resolvedAmount > 0 ? (
                formatCurrency(resolvedAmount, currency)
              ) : (
                <span className="text-lg text-gray-400">Amount defined by plan</span>
              )}
            </div>
            {planInterval && resolvedAmount > 0 && (
              <p className="text-sm text-gray-500 mt-1">Billed {planInterval}</p>
            )}
            {description && (
              <p className="text-sm text-gray-600 mt-2 border-t border-gray-200 pt-2">
                {description}
              </p>
            )}
          </div>

          {/* Error display */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Provider selection */}
          {!selectedProvider && !processing && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Select Payment Method
              </h3>

              {paymentConfig?.paystackEnabled && paymentConfig?.paystackPublicKey && (
                <button
                  onClick={() => {
                    setSelectedProvider('paystack');
                    setShowEmailInput(true);
                  }}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-gray-900">Paystack</div>
                      <div className="text-sm text-gray-500">Card payment</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {paymentConfig?.snapScanEnabled && paymentConfig?.snapScanConfig?.merchantId && (
                <button
                  onClick={handleSnapScanPayment}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zm8-2v8h8V3h-8zm6 6h-4V5h4v4zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm13-2h-2v3h-3v2h3v3h2v-3h3v-2h-3v-3z" />
                      </svg>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-gray-900">SnapScan</div>
                      <div className="text-sm text-gray-500">QR code payment</div>
                    </div>
                  </div>
                  <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}

              {!paymentConfig?.paystackEnabled && !paymentConfig?.snapScanEnabled && (
                <div className="text-center py-8">
                  <p className="text-gray-500">No payment methods are currently enabled</p>
                </div>
              )}
            </div>
          )}

          {/* Stage 1: Email input only */}
          {showEmailInput && selectedProvider === 'paystack' && !processing && paymentStage === 'email' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  required
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && userEmail && userEmail.includes('@')) {
                      hasPaymentBackend ? syncCustomer(userEmail) : (paymentMode === 'external' ? handleExternalPayment() : handlePaystackPayment());
                    }
                  }}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {hasPaymentBackend ? "We'll check if we have your details on file" : 'Payment receipt will be sent to this email'}
                </p>
              </div>

              <button
                onClick={() => hasPaymentBackend ? syncCustomer(userEmail) : (paymentMode === 'external' ? handleExternalPayment() : handlePaystackPayment())}
                disabled={!userEmail || !userEmail.includes('@') || customerSyncing || processing}
                className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
              >
                {customerSyncing ? 'Checking...' : processing ? 'Processing payment...' : 'Continue'}
              </button>
            </div>
          )}

          {/* Stage 2: Customer details form (if customer not found or data conflict) */}
          {showEmailInput && selectedProvider === 'paystack' && !processing && paymentStage === 'details' && (
            <div className="space-y-4">
              {/* Name fields */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Email field (read-only at this stage) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  value={userEmail}
                  readOnly
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Payment receipt will be sent to this email
                </p>
              </div>

              {/* Phone field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+27 12 345 6789"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => setPaymentStage('email')}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={paymentMode === 'external' ? handleExternalPayment : handlePaystackPayment}
                  disabled={processing}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {processing ? 'Processing payment...' : 'Continue to Payment'}
                </button>
              </div>
            </div>
          )}

          {/* Stage 3: Ready for payment (customer found with details) */}
          {showEmailInput && selectedProvider === 'paystack' && !processing && paymentStage === 'ready' && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-green-900">Welcome back!</h4>
                    <p className="text-sm text-green-700 mt-1">We found your details on file.</p>
                  </div>
                </div>
              </div>

              {/* Display customer details */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="text-sm">
                  <span className="text-gray-600">Name:</span>{' '}
                  <span className="text-gray-900 font-medium">{firstName} {lastName}</span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-600">Email:</span>{' '}
                  <span className="text-gray-900 font-medium">{userEmail}</span>
                </div>
                {phone && (
                  <div className="text-sm">
                    <span className="text-gray-600">Phone:</span>{' '}
                    <span className="text-gray-900 font-medium">{phone}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setPaymentStage('details');
                    // Keep the filled data so user can edit if needed
                  }}
                  className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm"
                >
                  Update Details
                </button>
                <button
                  onClick={paymentMode === 'external' ? handleExternalPayment : handlePaystackPayment}
                  disabled={processing}
                  className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {processing ? 'Processing payment...' : 'Continue to Payment'}
                </button>
              </div>
            </div>
          )}

          {/* Processing state - Paystack popup is open */}
          {processing && selectedProvider === 'paystack' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <svg className="animate-spin h-8 w-8 text-blue-600" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Processing Payment</h3>
              <p className="text-sm text-gray-600">Complete your payment in the Paystack popup window...</p>
            </div>
          )}

          {/* SnapScan QR Code */}
          {processing && selectedProvider === 'snapscan' && qrCodeUrl && (
            <div className="text-center py-4">
              <div className="mb-4">
                <img
                  src={qrCodeUrl}
                  alt="SnapScan QR Code"
                  className="mx-auto w-64 h-64 border-4 border-gray-200 rounded-lg"
                />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Scan to Pay</h3>
              <p className="text-sm text-gray-600">
                Open your SnapScan app and scan the QR code to complete payment
              </p>
              <div className="mt-4 flex items-center justify-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Waiting for payment confirmation...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Render modal in a portal at document.body level to escape Dialog z-index stacking
  return createPortal(modalContent, document.body);
};
